import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Plug, ScanSearch, Trash2, Pencil, Network, ShieldAlert, KeyRound, Copy, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", source_mode: "db_account_only",
  host: "", port: 3306, tls_mode: "disabled", username: "", password: "",
  default_database: "", connect_timeout: 10, read_timeout: 600, max_dump_duration: 7200,
  notes: "", enabled: true,
  // SSH tunnel (logical backups behind a jump host)
  ssh_tunnel_enabled: false,
  tunnel_remote_db_host: "", tunnel_remote_db_port: 3306,
  tunnel_local_bind_host: "127.0.0.1", tunnel_local_bind_port: "",
  tunnel_allow_public_bind: false,
  // SSH / physical
  ssh_host: "", ssh_port: 22, ssh_username: "root", ssh_auth_method: "key",
  ssh_private_key: "", ssh_password: "", ssh_key_passphrase: "",
  ssh_host_key_policy: "tofu", ssh_known_host_fingerprint: "",
  remote_tmp_dir: "/tmp", remote_staging_dir: "/tmp", remote_cleanup_policy: "always",
  remote_backup_timeout: 7200,
  sudo_mode: "none", sudo_path: "/usr/bin/sudo",
  runtime_mode: "systemd", mariadb_service_name: "mariadb", mariadb_socket: "",
  mariadb_datadir: "", mariadb_port: 3306, db_auth_method: "password",
};

export default function Sources() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({});
  const [testingForm, setTestingForm] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/sources").then((r) => setItems(r.data)).catch(() => toast.error(t("sources.loadFailed")));
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ ...EMPTY, ...s, password: "" }); setEditId(s.id); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, port: Number(form.port) };
      if (form.source_mode === "ssh_mariadb_host" && !payload.host) {
        payload.host = form.ssh_host;  // satisfy required field + show in list
      }
      // Always normalize tunnel ports so the API never receives "" for an int field
      // ("" = auto-allocate the local bind port → send null).
      payload.tunnel_remote_db_port = Number(form.tunnel_remote_db_port) || 3306;
      payload.tunnel_local_bind_port = form.tunnel_local_bind_port
        ? Number(form.tunnel_local_bind_port) : null;
      if (editId) {
        if (!payload.password) delete payload.password;
        if (!payload.ssh_private_key) delete payload.ssh_private_key;
        if (!payload.ssh_password) delete payload.ssh_password;
        if (!payload.ssh_key_passphrase) delete payload.ssh_key_passphrase;
        if (!payload.sudo_password) delete payload.sudo_password;
        await api.put(`/sources/${editId}`, payload);
        toast.success(t("sources.saveUpdated"));
      } else {
        await api.post("/sources", payload);
        toast.success(t("sources.saveCreated"));
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sources.saveFailed"));
    } finally { setSaving(false); }
  };

  const testSSH = async () => {
    if (!form.ssh_host || !form.ssh_username) { toast.error(t("sources.sshHostUserReq")); return; }
    setTestingForm(true);
    try {
      const r = await api.post("/sources/ssh-test", {
        ssh_host: form.ssh_host, ssh_port: Number(form.ssh_port) || 22,
        ssh_username: form.ssh_username, ssh_auth_method: form.ssh_auth_method,
        ssh_private_key: form.ssh_private_key || undefined,
        ssh_password: form.ssh_password || undefined,
        ssh_key_passphrase: form.ssh_key_passphrase || undefined,
        ssh_host_key_policy: form.ssh_host_key_policy,
        ssh_known_host_fingerprint: form.ssh_known_host_fingerprint || undefined,
        source_id: editId || undefined,
      });
      if (r.data.ok) {
        toast.success(t("sources.sshOk", { os: r.data.os || "", arch: r.data.arch || "", user: r.data.user || "" }));
        if (r.data.host_key_fingerprint) setForm((f) => ({ ...f, ssh_known_host_fingerprint: r.data.host_key_fingerprint }));
      } else {
        toast.error(r.data.remediation || r.data.error || t("sources.sshFailed"));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || t("sources.sshTestFailed")); }
    finally { setTestingForm(false); }
  };

  const testConn = async (id) => {
    setBusy((b) => ({ ...b, [id]: "test" }));
    try {
      const r = await api.post(`/sources/${id}/test`);
      r.data.ok ? toast.success(t("sources.connected", { version: r.data.version })) : toast.error(r.data.error || t("sources.connFailed"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sources.testFailed")); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  const testForm = async () => {
    if (!form.host || !form.username) { toast.error(t("sources.hostUserReq")); return; }
    setTestingForm(true);
    try {
      const r = await api.post("/sources/test", {
        host: form.host, port: Number(form.port), tls_mode: form.tls_mode,
        username: form.username, password: form.password || undefined,
        default_database: form.default_database || undefined,
        ca_cert: form.ca_cert || undefined, client_cert: form.client_cert || undefined,
        client_key: form.client_key || undefined,
        connect_timeout: Number(form.connect_timeout) || 10,
        source_id: editId || undefined,
      });
      r.data.ok
        ? toast.success(t("sources.connected", { version: `${r.data.version}${r.data.current_user ? ` · ${r.data.current_user}` : ""}` }))
        : toast.error(r.data.error || t("sources.connFailed"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("sources.testFailed")); }
    finally { setTestingForm(false); }
  };

  const testTunnel = async () => {
    if (!form.ssh_host || !form.ssh_username) { toast.error(t("sources.tunnelSshReq")); return; }
    if (!form.username) { toast.error(t("sources.tunnelDbReq")); return; }
    setTestingForm(true);
    try {
      const r = await api.post("/sources/tunnel-test", {
        ssh_host: form.ssh_host, ssh_port: Number(form.ssh_port) || 22,
        ssh_username: form.ssh_username, ssh_auth_method: form.ssh_auth_method,
        ssh_private_key: form.ssh_private_key || undefined,
        ssh_password: form.ssh_password || undefined,
        ssh_key_passphrase: form.ssh_key_passphrase || undefined,
        ssh_host_key_policy: form.ssh_host_key_policy,
        ssh_known_host_fingerprint: form.ssh_known_host_fingerprint || undefined,
        tunnel_remote_db_host: form.tunnel_remote_db_host || undefined,
        tunnel_remote_db_port: Number(form.tunnel_remote_db_port) || 3306,
        tunnel_local_bind_host: form.tunnel_local_bind_host || "127.0.0.1",
        tunnel_local_bind_port: form.tunnel_local_bind_port ? Number(form.tunnel_local_bind_port) : undefined,
        tunnel_allow_public_bind: !!form.tunnel_allow_public_bind,
        host: form.host || undefined, port: Number(form.port) || 3306,
        tls_mode: form.tls_mode, username: form.username,
        password: form.password || undefined,
        default_database: form.default_database || undefined,
        source_id: editId || undefined,
      });
      if (r.data.ok) {
        toast.success(t("sources.tunnelOk", { version: r.data.version || "", local: r.data.local_bind || "", remote: r.data.remote_target || "" }));
        if (r.data.host_key_fingerprint && !form.ssh_known_host_fingerprint)
          setForm((f) => ({ ...f, ssh_known_host_fingerprint: r.data.host_key_fingerprint }));
      } else {
        toast.error(`[${r.data.step || "tunnel"}] ${r.data.remediation || r.data.error || t("sources.tunnelFailed")}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || t("sources.tunnelFailed")); }
    finally { setTestingForm(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("sources.confirmDelete"))) return;
    try { await api.delete(`/sources/${id}`); toast.success(t("sources.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("sources.deleteFailed")); }
  };

  return (
    <div data-testid="sources-page">
      <PageHeader
        title={t("sources.title")}
        subtitle={t("sources.subtitle")}
        actions={
          <Button data-testid="add-source-button" onClick={openCreate} className="rounded-none uppercase font-semibold tracking-wide bg-[#0A0A0A] hover:bg-[#0052FF]">
            <Plus className="h-4 w-4 mr-1.5" /> {t("sources.addSource")}
          </Button>
        }
      />
      <div className="p-6 md:p-8">
        {items.length === 0 ? (
          <div className="grid-panel p-16 text-center">
            <p className="text-muted-foreground">{t("sources.noSources")}</p>
            <Button onClick={openCreate} className="mt-4 rounded-none uppercase" data-testid="empty-add-source">{t("sources.addFirst")}</Button>
          </div>
        ) : (
          <div className="grid-panel overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-5 py-3 font-medium">{t("sources.thName")}</th>
                  <th className="px-5 py-3 font-medium">{t("sources.thHost")}</th>
                  <th className="px-5 py-3 font-medium">{t("sources.thUser")}</th>
                  <th className="px-5 py-3 font-medium">{t("sources.thCredentials")}</th>
                  <th className="px-5 py-3 font-medium">{t("sources.thLastScan")}</th>
                  <th className="px-5 py-3 font-medium text-right">{t("sources.thActions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="data-grid-row" data-testid={`source-row-${s.id}`}>
                    <td className="px-5 py-3 font-medium cursor-pointer hover:text-brand" onClick={() => navigate(`/sources/${s.id}`)}>
                      {s.name}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">{s.host}:{s.port}</td>
                    <td className="px-5 py-3 font-mono text-xs">{s.username}</td>
                    <td className="px-5 py-3"><StatusBadge status={s.credential_status || "untested"} /></td>
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{fmtDate(s.last_scanned)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="rounded-none h-8" data-testid={`test-source-${s.id}`}
                          disabled={busy[s.id] === "test"} onClick={() => testConn(s.id)}>
                          <Plug className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8" data-testid={`scan-source-${s.id}`}
                          onClick={() => navigate(`/sources/${s.id}`)}>
                          <ScanSearch className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8" data-testid={`edit-source-${s.id}`}
                          onClick={() => openEdit(s)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8 text-destructive" data-testid={`delete-source-${s.id}`}
                          onClick={() => del(s.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="source-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? t("sources.editTitle") : t("sources.newTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <Field label={t("sources.name")}><Input data-testid="source-name" value={form.name} onChange={set("name")} className="rounded-none" /></Field>

            {/* Source type */}
            <Field label={t("sources.sourceType")}>
              <div className="grid grid-cols-2 gap-px bg-border border border-border">
                {[["db_account_only", t("sources.modeDbOnly"), t("sources.modeDbOnlySub")],
                  ["ssh_mariadb_host", t("sources.modeSsh"), t("sources.modeSshSub")]].map(([v, tt, sub]) => (
                  <button key={v} type="button" data-testid={`source-mode-${v}`}
                    onClick={() => set("source_mode")(v)}
                    className={`px-3 py-2.5 text-left transition-colors ${form.source_mode === v ? "bg-[#0A0A0A] text-white" : "bg-card hover:bg-secondary"}`}>
                    <span className="block text-sm font-semibold">{tt}</span>
                    <span className={`block font-mono text-[10px] ${form.source_mode === v ? "text-white/60" : "text-muted-foreground"}`}>{sub}</span>
                  </button>
                ))}
              </div>
            </Field>

            {form.source_mode === "db_account_only" ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="col-span-2"><Field label={t("sources.host")}><Input data-testid="source-host" value={form.host} onChange={set("host")} className="rounded-none" placeholder="db.example.com" /></Field></div>
                  <Field label={t("sources.port")}><Input data-testid="source-port" type="number" value={form.port} onChange={set("port")} className="rounded-none" /></Field>
                </div>
                <Field label={t("sources.tlsMode")}>
                  <Select value={form.tls_mode} onValueChange={set("tls_mode")}>
                    <SelectTrigger className="rounded-none" data-testid="source-tls"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["disabled", "preferred", "required", "verify_ca", "verify_identity"].map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("sources.username")}><Input data-testid="source-username" value={form.username} onChange={set("username")} className="rounded-none" /></Field>
                  <Field label={editId ? t("sources.passwordKeep") : t("sources.password")}>
                    <Input data-testid="source-password" type="password" value={form.password} onChange={set("password")} className="rounded-none" />
                  </Field>
                </div>
                <Field label={t("sources.defaultDb")}><Input data-testid="source-defaultdb" value={form.default_database || ""} onChange={set("default_database")} className="rounded-none" /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label={t("sources.connectTo")}><Input type="number" value={form.connect_timeout} onChange={set("connect_timeout")} className="rounded-none" /></Field>
                  <Field label={t("sources.readTo")}><Input type="number" value={form.read_timeout} onChange={set("read_timeout")} className="rounded-none" /></Field>
                  <Field label={t("sources.maxDump")}><Input type="number" value={form.max_dump_duration} onChange={set("max_dump_duration")} className="rounded-none" /></Field>
                </div>
                <TunnelFields form={form} set={set} editId={editId} onTest={testTunnel} testing={testingForm} />
              </>
            ) : (
              <SSHFields form={form} set={set} editId={editId} onTestSSH={testSSH} testing={testingForm} />
            )}

            <Field label={t("sources.notes")}><Textarea value={form.notes} onChange={set("notes")} className="rounded-none" rows={2} /></Field>
            <div className="flex items-center justify-between">
              <Label>{t("sources.enabled")}</Label>
              <Switch checked={form.enabled} onCheckedChange={set("enabled")} data-testid="source-enabled" />
            </div>
            <p className="font-mono text-[11px] text-muted-foreground border border-border bg-secondary p-3">
              {form.source_mode === "ssh_mariadb_host"
                ? t("sources.grantSsh")
                : t("sources.grantLogical")}
            </p>
          </div>
          <DialogFooter>
            {form.source_mode === "ssh_mariadb_host" ? (
              <Button variant="outline" className="rounded-none mr-auto" onClick={testSSH} disabled={testingForm} data-testid="dialog-test-ssh-button">
                <Plug className="h-4 w-4 mr-1.5" /> {testingForm ? t("common.testing") : t("sources.testSsh")}
              </Button>
            ) : form.ssh_tunnel_enabled ? (
              <Button variant="outline" className="rounded-none mr-auto" onClick={testTunnel} disabled={testingForm} data-testid="dialog-test-tunnel-button">
                <Plug className="h-4 w-4 mr-1.5" /> {testingForm ? t("common.testing") : t("sources.testTunnel")}
              </Button>
            ) : (
              <Button variant="outline" className="rounded-none mr-auto" onClick={testForm} disabled={testingForm} data-testid="dialog-test-connection-button">
                <Plug className="h-4 w-4 mr-1.5" /> {testingForm ? t("common.testing") : t("sources.testConnection")}
              </Button>
            )}
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button data-testid="save-source-button" onClick={save} disabled={saving} className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]">
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SSHFields({ form, set, editId, onTestSSH, testing }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 border-l-2 border-brand/40 pl-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-brand">{t("sources.sshConnection")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="col-span-2"><Field label={t("sources.sshHost")}><Input data-testid="ssh-host" value={form.ssh_host} onChange={set("ssh_host")} className="rounded-none" placeholder="db-server.internal" /></Field></div>
        <Field label={t("sources.sshPort")}><Input data-testid="ssh-port" type="number" value={form.ssh_port} onChange={set("ssh_port")} className="rounded-none" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.sshUsername")}><Input data-testid="ssh-username" value={form.ssh_username} onChange={set("ssh_username")} className="rounded-none" /></Field>
        <Field label={t("sources.authMethod")}>
          <Select value={form.ssh_auth_method} onValueChange={set("ssh_auth_method")}>
            <SelectTrigger className="rounded-none" data-testid="ssh-auth-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="key">{t("sources.authKey")}</SelectItem><SelectItem value="password">{t("sources.authPassword")}</SelectItem></SelectContent>
          </Select>
        </Field>
      </div>
      {form.ssh_auth_method === "key" ? (
        <>
          <Field label={editId ? t("sources.privKeyKeep") : t("sources.privKeyNew")}>
            <textarea data-testid="ssh-private-key" value={form.ssh_private_key} onChange={set("ssh_private_key")} rows={3}
              className="w-full rounded-none border border-input p-2 font-mono text-[11px]" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" />
          </Field>
          <KeyGenBlock set={set} editId={editId} testid="ssh" />
          <Field label={t("sources.keyPassphrase")}><Input data-testid="ssh-key-passphrase" type="password" value={form.ssh_key_passphrase} onChange={set("ssh_key_passphrase")} className="rounded-none" /></Field>
        </>
      ) : (
        <Field label={editId ? t("sources.sshPwdKeep") : t("sources.sshPwd")}><Input data-testid="ssh-password" type="password" value={form.ssh_password} onChange={set("ssh_password")} className="rounded-none" /></Field>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.hostKeyPolicy")}>
          <Select value={form.ssh_host_key_policy} onValueChange={set("ssh_host_key_policy")}>
            <SelectTrigger className="rounded-none" data-testid="ssh-hostkey-policy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="strict">{t("sources.policyStrict")}</SelectItem>
              <SelectItem value="tofu">{t("sources.policyTofu")}</SelectItem>
              <SelectItem value="insecure">{t("sources.policyInsecure")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-end">
          <Button type="button" variant="outline" className="rounded-none w-full" onClick={onTestSSH} disabled={testing} data-testid="ssh-test-inline">
            <Plug className="h-3.5 w-3.5 mr-1.5" />{testing ? t("common.testing") : t("sources.testSsh")}
          </Button>
        </div>
      </div>
      {form.ssh_known_host_fingerprint && (
        <p className="font-mono text-[10px] text-muted-foreground break-all">{t("sources.pinned", { fp: form.ssh_known_host_fingerprint })}</p>
      )}

      <p className="font-mono text-[10px] uppercase tracking-wider text-brand pt-2">{t("sources.mariadbRuntime")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.runtimeMode")}>
          <Select value={form.runtime_mode} onValueChange={set("runtime_mode")}>
            <SelectTrigger className="rounded-none" data-testid="runtime-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="systemd">{t("sources.rtSystemd")}</SelectItem>
              <SelectItem value="direct">{t("sources.rtDirect")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {form.runtime_mode === "systemd"
          ? <Field label={t("sources.serviceName")}><Input data-testid="mariadb-service" value={form.mariadb_service_name} onChange={set("mariadb_service_name")} className="rounded-none" /></Field>
          : <Field label={t("sources.mariadbPort")}><Input type="number" value={form.mariadb_port} onChange={set("mariadb_port")} className="rounded-none" /></Field>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.socketPath")}><Input data-testid="mariadb-socket" value={form.mariadb_socket} onChange={set("mariadb_socket")} className="rounded-none" placeholder="/run/mysqld/mysqld.sock" /></Field>
        <Field label={t("sources.datadir")}><Input value={form.mariadb_datadir} onChange={set("mariadb_datadir")} className="rounded-none" /></Field>
      </div>

      <p className="font-mono text-[10px] uppercase tracking-wider text-brand pt-2">{t("sources.backupCreds")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.dbUsername")}><Input data-testid="ssh-db-username" value={form.username} onChange={set("username")} className="rounded-none" /></Field>
        <Field label={t("sources.dbAuth")}>
          <Select value={form.db_auth_method} onValueChange={set("db_auth_method")}>
            <SelectTrigger className="rounded-none" data-testid="db-auth-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="password">{t("sources.dbAuthPassword")}</SelectItem><SelectItem value="socket">{t("sources.dbAuthSocket")}</SelectItem></SelectContent>
          </Select>
        </Field>
      </div>
      {form.db_auth_method === "password" && (
        <Field label={editId ? t("sources.dbPwdKeep") : t("sources.dbPwd")}><Input data-testid="ssh-db-password" type="password" value={form.password} onChange={set("password")} className="rounded-none" /></Field>
      )}

      <p className="font-mono text-[10px] uppercase tracking-wider text-brand pt-2">{t("sources.privRemote")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.sudoMode")}>
          <Select value={form.sudo_mode} onValueChange={set("sudo_mode")}>
            <SelectTrigger className="rounded-none" data-testid="sudo-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("sources.sudoNone")}</SelectItem>
              <SelectItem value="nopasswd">{t("sources.sudoNopasswd")}</SelectItem>
              <SelectItem value="password">{t("sources.sudoPassword")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("sources.cleanupPolicy")}>
          <Select value={form.remote_cleanup_policy} onValueChange={set("remote_cleanup_policy")}>
            <SelectTrigger className="rounded-none" data-testid="cleanup-policy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="always">{t("sources.cleanupAlways")}</SelectItem>
              <SelectItem value="on_success">{t("sources.cleanupOnSuccess")}</SelectItem>
              <SelectItem value="never">{t("sources.cleanupNever")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      {form.sudo_mode === "password" && (
        <Field label={editId ? t("sources.sudoPwdKeep") : t("sources.sudoPwd")}><Input data-testid="sudo-password" type="password" value={form.sudo_password || ""} onChange={set("sudo_password")} className="rounded-none" /></Field>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("sources.remoteTmp")}><Input data-testid="remote-tmp-dir" value={form.remote_tmp_dir} onChange={set("remote_tmp_dir")} className="rounded-none" /></Field>
        <Field label={t("sources.remoteStaging")}><Input data-testid="remote-staging-dir" value={form.remote_staging_dir} onChange={set("remote_staging_dir")} className="rounded-none" /></Field>
      </div>
    </div>
  );
}

function TunnelFields({ form, set, editId, onTest, testing }) {
  const { t } = useTranslation();
  const nonLoopbackBind = form.tunnel_local_bind_host && form.tunnel_local_bind_host !== "127.0.0.1"
    && form.tunnel_local_bind_host !== "localhost" && form.tunnel_local_bind_host !== "::1"
    && !form.tunnel_local_bind_host.startsWith("127.");
  return (
    <div className="border border-border bg-secondary/40">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-brand" />
          <div>
            <span className="block text-sm font-semibold">{t("sources.tunnelTitle")}</span>
            <span className="block font-mono text-[10px] text-muted-foreground">{t("sources.tunnelSub")}</span>
          </div>
        </div>
        <Switch checked={!!form.ssh_tunnel_enabled} onCheckedChange={set("ssh_tunnel_enabled")} data-testid="tunnel-enabled" />
      </div>

      {form.ssh_tunnel_enabled && (
        <div className="space-y-4 p-3" data-testid="tunnel-fields">
          <p className="font-mono text-[10px] text-muted-foreground leading-relaxed">
            {t("sources.tunnelExplain")}
          </p>

          <p className="font-mono text-[10px] uppercase tracking-wider text-brand">{t("sources.jumpServer")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="col-span-2"><Field label={t("sources.sshHost")}><Input data-testid="tunnel-ssh-host" value={form.ssh_host} onChange={set("ssh_host")} className="rounded-none" placeholder="app-server.example.com" /></Field></div>
            <Field label={t("sources.sshPort")}><Input data-testid="tunnel-ssh-port" type="number" value={form.ssh_port} onChange={set("ssh_port")} className="rounded-none" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("sources.sshUsername")}><Input data-testid="tunnel-ssh-username" value={form.ssh_username} onChange={set("ssh_username")} className="rounded-none" /></Field>
            <Field label={t("sources.authMethod")}>
              <Select value={form.ssh_auth_method} onValueChange={set("ssh_auth_method")}>
                <SelectTrigger className="rounded-none" data-testid="tunnel-ssh-auth"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="key">{t("sources.authKey")}</SelectItem><SelectItem value="password">{t("sources.authPassword")}</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          {form.ssh_auth_method === "key" ? (
            <>
              <Field label={editId ? t("sources.privKeyKeep") : t("sources.privKeyNew")}>
                <textarea data-testid="tunnel-ssh-key" value={form.ssh_private_key} onChange={set("ssh_private_key")} rows={3}
                  className="w-full rounded-none border border-input p-2 font-mono text-[11px]" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" />
              </Field>
              <KeyGenBlock set={set} editId={editId} testid="tunnel" />
              <Field label={t("sources.keyPassphrase")}><Input data-testid="tunnel-ssh-passphrase" type="password" value={form.ssh_key_passphrase} onChange={set("ssh_key_passphrase")} className="rounded-none" /></Field>
            </>
          ) : (
            <Field label={editId ? t("sources.sshPwdKeep") : t("sources.sshPwd")}><Input data-testid="tunnel-ssh-password" type="password" value={form.ssh_password} onChange={set("ssh_password")} className="rounded-none" /></Field>
          )}
          <Field label={t("sources.hostKeyPolicy")}>
            <Select value={form.ssh_host_key_policy} onValueChange={set("ssh_host_key_policy")}>
              <SelectTrigger className="rounded-none" data-testid="tunnel-hostkey-policy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="strict">{t("sources.policyStrict")}</SelectItem>
                <SelectItem value="tofu">{t("sources.policyTofu")}</SelectItem>
                <SelectItem value="insecure">{t("sources.policyInsecure")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {form.ssh_known_host_fingerprint && (
            <p className="font-mono text-[10px] text-muted-foreground break-all">{t("sources.pinned", { fp: form.ssh_known_host_fingerprint })}</p>
          )}

          <p className="font-mono text-[10px] uppercase tracking-wider text-brand pt-1">{t("sources.dbAsSeen")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="col-span-2"><Field label={t("sources.remoteDbHost")}><Input data-testid="tunnel-remote-host" value={form.tunnel_remote_db_host ?? ""} onChange={set("tunnel_remote_db_host")} className="rounded-none" placeholder="10.0.0.10 / db.internal / 127.0.0.1" /></Field></div>
            <Field label={t("sources.remoteDbPort")}><Input data-testid="tunnel-remote-port" type="number" value={form.tunnel_remote_db_port ?? 3306} onChange={set("tunnel_remote_db_port")} className="rounded-none" /></Field>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-wider text-brand pt-1">{t("sources.localBind")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("sources.localBindHost")}><Input data-testid="tunnel-bind-host" value={form.tunnel_local_bind_host ?? "127.0.0.1"} onChange={set("tunnel_local_bind_host")} className="rounded-none" /></Field>
            <Field label={t("sources.localBindPort")}><Input data-testid="tunnel-bind-port" type="number" value={form.tunnel_local_bind_port ?? ""} onChange={set("tunnel_local_bind_port")} className="rounded-none" placeholder="auto" /></Field>
          </div>
          {nonLoopbackBind && (
            <div className="flex items-start gap-2 border border-warning/50 bg-warning/10 p-2.5" data-testid="tunnel-bind-warning">
              <ShieldAlert className="h-4 w-4 text-yellow-700 shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <p className="text-[11px] text-yellow-800 leading-relaxed">
                  {t("sources.bindWarn")}
                </p>
                <label className="flex items-center gap-2 text-[11px] font-mono text-yellow-800">
                  <input type="checkbox" data-testid="tunnel-allow-public" checked={!!form.tunnel_allow_public_bind}
                    onChange={(e) => set("tunnel_allow_public_bind")(e.target.checked)} />
                  {t("sources.allowPublic")}
                </label>
              </div>
            </div>
          )}

          <Button type="button" variant="outline" className="rounded-none w-full" onClick={onTest} disabled={testing} data-testid="tunnel-test-inline">
            <Plug className="h-3.5 w-3.5 mr-1.5" />{testing ? t("common.testing") : t("sources.testTunnel")}
          </Button>
        </div>
      )}
    </div>
  );
}

function KeyGenBlock({ set, editId, testid }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [pubKey, setPubKey] = useState("");

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sources/ssh/generate-key");
      set("ssh_private_key")(r.data.private_key);
      set("ssh_key_passphrase")("");
      setPubKey(r.data.public_key);
      toast.success(t("sources.keyGenerated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sources.failed"));
    } finally {
      setBusy(false);
    }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(t("sources.copied")); };

  return (
    <div className="space-y-2 -mt-1">
      <Button type="button" variant="outline" size="sm" className="rounded-none"
        onClick={generate} disabled={busy} data-testid={`${testid}-generate-key`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
        {t("sources.generateKey")}
      </Button>
      {pubKey && (
        <div className="border border-border bg-secondary p-3 space-y-2" data-testid={`${testid}-public-key`}>
          <p className="text-[11px] text-muted-foreground">{t("sources.publicKeyHint")}</p>
          <div className="flex items-start gap-2 bg-[#0A0A0A] p-2">
            <code className="flex-1 font-mono text-[10px] text-green-400 break-all">{pubKey}</code>
            <button type="button" onClick={() => copy(pubKey)} className="text-white/50 hover:text-white shrink-0" data-testid={`${testid}-copy-public-key`}><Copy className="h-3.5 w-3.5" /></button>
          </div>
          <code className="block font-mono text-[10px] text-muted-foreground break-all">echo "{pubKey}" &gt;&gt; ~/.ssh/authorized_keys</code>
          <p className="text-[11px] text-yellow-700">{t("sources.privKeySaveHint")}</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}